import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Stethoscope, Users, ChartLineUp, Briefcase } from "@phosphor-icons/react";
import KineticText from "@/components/motion/KineticText";
import Reveal from "@/components/motion/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import { T, useContent } from "@/content/ContentContext";

const HERO_IMG = "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMHNreWxpbmUlMjBtb2Rlcm58ZW58MHx8fHwxNzg3NzM2NTM4fDA&ixlib=rb-4.1.0&q=85";
const FOUNDER_IMG = "/natasha-bagier.jpg";
const CLINIC_IMG = "https://images.unsplash.com/photo-1781513144825-aa1e284c5950?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjbGluaWMlMjBpbnRlcmlvciUyMGx1eHVyeXxlbnwwfHx8fDE3ODc3MzY1Mzh8MA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { n: "01", icon: Stethoscope, k: "home.service.1", to: "/licensing" },
  { n: "02", icon: Users, k: "home.service.2", to: "/recruitment" },
  { n: "03", icon: ChartLineUp, k: "home.service.3", to: "/consulting" },
  { n: "04", icon: Briefcase, k: "home.service.4", to: "/consulting" },
];

const STATS = [
  { k: "home.stats.1" },
  { k: "home.stats.2" },
  { k: "home.stats.3" },
  { k: "home.stats.4" },
];

export default function HomePage() {
  const { t } = useContent();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen bg-brand-night text-brand-bone texture-grain overflow-hidden flex items-center pt-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center w-full">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="overline-label text-brand-gold"
              data-testid="hero-overline"
            >
              {t("home.hero.overline")}
            </motion.p>
            <KineticText
              data-testid="hero-headline"
              className="mt-8 font-serif text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tight font-medium"
              lines={[
                <T key="1" k="home.hero.line1" />,
                <T key="2" k="home.hero.line2" />,
                <T key="3" k="home.hero.line3" em="text-brand-gold font-semibold" />,
              ]}
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.65 }}
              className="mt-8 max-w-xl text-base lg:text-lg leading-relaxed font-light text-brand-bone/75"
              data-testid="hero-subtext"
            >
              {t("home.hero.sub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-3 rounded-full bg-brand-gold px-8 py-4 text-sm font-semibold text-brand-night transition-all duration-300 hover:bg-brand-goldlight hover:-translate-y-0.5"
              >
                {t("home.hero.ctaPrimary")} <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/licensing"
                data-testid="hero-cta-secondary"
                className="text-sm font-semibold text-brand-bone/80 border-b border-brand-gold/50 pb-1 transition-colors duration-300 hover:text-brand-gold"
              >
                {t("home.hero.ctaSecondary")}
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative hidden lg:block">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -top-4 -left-4 h-full w-full border border-brand-gold/40" aria-hidden="true" />
              <div className="relative overflow-hidden h-[520px]">
                <motion.img
                  src={HERO_IMG}
                  alt="Dubai skyline at dusk"
                  style={{ y: imgY }}
                  className="h-[120%] w-full object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-brand-night/20" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-brand-green px-8 py-6 max-w-[240px]">
                <p className="font-serif text-xl italic text-brand-bone leading-snug">"{t("home.hero.quote")}"</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* MANIFESTO INTRO */}
      <section className="bg-brand-bone py-28 lg:py-36">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Reveal>
              <p className="overline-label text-brand-green mt-3">{t("home.manifesto.label")}</p>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-brand-ink font-medium" data-testid="manifesto-heading">
                <T k="home.manifesto.heading" em="text-brand-green" />
              </h2>
              <p className="mt-8 max-w-2xl text-base lg:text-lg font-light leading-relaxed text-brand-ink/70">
                {t("home.manifesto.body")}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES CHAPTERS */}
      <section className="bg-brand-deep text-brand-bone py-28 lg:py-36 texture-grain">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-4xl lg:text-6xl tracking-tight font-medium mt-4" data-testid="services-heading">{t("home.services.heading")}</h2>
          </Reveal>
          <div className="mt-16 border-t border-brand-bone/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <Link
                  to={s.to}
                  data-testid={`service-chapter-${s.n}`}
                  className="group grid grid-cols-1 md:grid-cols-12 gap-6 items-center py-10 border-b border-brand-bone/10 transition-colors duration-500 hover:bg-brand-bone/[0.03] px-2 sm:px-4"
                >
                  <span className="md:col-span-1 font-sans text-sm font-bold text-brand-gold tracking-[0.2em]">{s.n}</span>
                  <div className="md:col-span-4 flex items-center gap-4">
                    <s.icon size={30} weight="duotone" className="text-brand-gold shrink-0" />
                    <h3 className="font-serif text-2xl lg:text-3xl font-medium">{t(`${s.k}.title`)}</h3>
                  </div>
                  <p className="md:col-span-6 text-sm lg:text-base font-light leading-relaxed text-brand-bone/65">{t(`${s.k}.body`)}</p>
                  <span className="md:col-span-1 justify-self-start md:justify-self-end text-brand-gold transition-transform duration-300 group-hover:translate-x-2">
                    <ArrowRight size={22} weight="bold" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="bg-brand-bone py-28 lg:py-36">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 relative">
            <Reveal>
              <div className="absolute -top-4 -right-4 h-full w-full border border-brand-gold/50" aria-hidden="true" />
              <img src={FOUNDER_IMG} alt="Natasha Bagier, founder of Mayfair Medical Services" className="relative h-[520px] w-full object-cover object-top" data-testid="founder-image" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-night/85 to-transparent px-6 pt-16 pb-5">
                <p className="font-serif text-xl text-brand-bone">{t("about.founder.name")}</p>
                <p className="text-[10px] uppercase tracking-[0.3em] text-brand-gold mt-1">{t("home.founder.caption")}</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] font-medium text-brand-ink mt-4" data-testid="founder-heading">
                <T k="home.founder.heading" em="text-brand-green" />
              </h2>
              <p className="mt-8 max-w-2xl text-base lg:text-lg font-light leading-relaxed text-brand-ink/70">
                {t("home.founder.body")}
              </p>
              <Link
                to="/about"
                data-testid="founder-cta"
                className="mt-10 inline-flex items-center gap-3 text-sm font-semibold text-brand-green border-b border-brand-gold pb-1 transition-colors duration-300 hover:text-brand-gold"
              >
                {t("home.founder.cta")} <ArrowRight size={16} weight="bold" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-brand-bone pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-brand-ink/10">
            {STATS.map((s, i) => (
              <Reveal key={s.k} delay={i * 0.08} className="border-b lg:border-b-0 border-r last:border-r-0 border-brand-ink/10 px-6 py-10">
                <p className="font-serif text-5xl lg:text-6xl text-brand-green font-medium" data-testid={`stat-value-${i}`}>{t(`${s.k}.value`)}</p>
                <p className="mt-3 text-xs uppercase tracking-[0.2em] text-brand-ink/50">{t(`${s.k}.label`)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-brand-night text-brand-bone py-28 lg:py-36 texture-grain overflow-hidden">
        <img src={CLINIC_IMG} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium max-w-3xl leading-[1.05]" data-testid="cta-heading">
              <T k="home.cta.heading" em="text-brand-gold" />
            </h2>
            <p className="mt-6 max-w-xl text-base lg:text-lg font-light text-brand-bone/70">
              {t("home.cta.body")}
            </p>
            <Link
              to="/contact"
              data-testid="cta-button"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-brand-gold px-9 py-4 text-sm font-semibold text-brand-night transition-all duration-300 hover:bg-brand-goldlight hover:-translate-y-0.5"
            >
              {t("home.cta.button")} <ArrowRight size={16} weight="bold" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
